import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { DateTime } from 'luxon'
import isUndefined from 'lodash/isUndefined'
import omitBy from 'lodash/omitBy'
import Box from '@material-ui/core/Box'
import FormControl from '@material-ui/core/FormControl'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Grid from '@material-ui/core/Grid'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import Select from '@material-ui/core/Select'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import ToggleButton from '@material-ui/lab/ToggleButton'
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup'
import { makeStyles } from '@material-ui/core/styles'
import { LocalizationProvider, DesktopDatePicker } from '@mui/x-date-pickers'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import AddOutlinedIcon from 'components/material-symbol/icons/Add'
import RepeatIcon from 'components/material-symbol/icons/Repeat'
import EventIcon from 'components/material-symbol/icons/Event'
import TonalButton from 'components/empty-page-state/tonal-button'
import PGATimePicker from 'components/pga-time-picker'
import SessionInput from 'components/multi-session-input/session-input'
import {
  mergeDesktopDatePickerSlotProps,
  mergeDesktopDatePickerSlots,
} from 'components/pga-time-picker/desktop-date-picker'
import CustomLuxonAdapter from 'lib/CustomLuxonAdapter'
import {
  outlinedPickerFieldSx,
  OUTLINED_INPUT_BORDER_RADIUS,
} from 'lib/form-field-styles'
import pluralize from 'lib/pluralize'
import { calendarDatePickerTheme } from 'pages/pga-coach/bookings/schedule/calendar/calendar-date-picker-theme'
import { useStyles as useFormStyles } from 'pages/pga-coach/bookings/events/edit/edit-event-form/styles'
import {
  defaultRecurrenceRule,
  END_TYPE_COUNT,
  END_TYPE_DATE,
  generateOccurrences,
  MAX_RECURRING_DATES,
  MIN_RECURRING_DATES,
  WEEKDAYS,
} from './generate-occurrences'

const RULE = 'recurrenceRule'
const RULE_FIELDS = [
  'startDate',
  'startTime',
  'endTime',
  'intervalWeeks',
  'weekdays',
  'endType',
  'endDate',
  'count',
]
const DATE_ROW_FORMAT = 'EEE, LLL d, yyyy'

const useStyles = makeStyles(theme => ({
  builder: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(3),
  },
  builderRow: {
    marginBottom: theme.spacing(2),
  },
  label: {
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.75),
  },
  // Overrides the app-wide segmented ToggleButtonGroup theme so each day is
  // its own round chip.
  weekdays: {
    display: 'flex',
    gap: theme.spacing(0.75),
    flexWrap: 'wrap',
    border: 'none',
    borderRadius: 0,
    overflow: 'visible',
    backgroundColor: 'transparent',
    '&& $weekday': {
      width: 40,
      minWidth: 40,
      height: 40,
      flex: '0 0 auto',
      padding: 0,
      margin: 0,
      borderRadius: '50% !important',
      border: `1px solid ${theme.palette.divider} !important`,
      fontWeight: 600,
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.common.white,
    },
    '&& $weekday.Mui-selected, && $weekday.Mui-selected:hover': {
      backgroundColor: '#0053FF',
      borderColor: '#0053FF !important',
      color: theme.palette.common.white,
    },
  },
  weekday: {},
  endsRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  endsOption: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  endDatePicker: {
    width: 200,
  },
  countInput: {
    width: 90,
  },
  generateRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  listHeader: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  count: {
    fontSize: '1rem',
    fontWeight: 600,
  },
  rowMeta: {
    marginTop: theme.spacing(-1.5),
    marginBottom: theme.spacing(1.5),
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  error: {
    color: theme.palette.error.main,
    marginBottom: theme.spacing(1.5),
  },
}))

const noop = () => {}

const toDateTime = value => {
  if (!value) return null
  if (DateTime.isDateTime(value)) return value
  const parsed = DateTime.fromJSDate(new Date(value))
  return parsed.isValid ? parsed : null
}

const RuleDatePicker = ({ name, label, defaultValue, className }) => {
  const { control } = useFormContext()

  return (
    <LocalizationProvider dateAdapter={CustomLuxonAdapter}>
      <ThemeProvider
        theme={createTheme(calendarDatePickerTheme, {
          shape: { borderRadius: OUTLINED_INPUT_BORDER_RADIUS },
        })}
      >
        <Controller
          control={control}
          name={name}
          defaultValue={defaultValue}
          render={({ onChange, value }) => (
            <DesktopDatePicker
              label={label}
              value={value}
              onChange={onChange}
              disablePast
              format="LLL d, yyyy"
              dayOfWeekFormatter={day => `${day}`}
              closeOnSelect
              className={className}
              sx={{ width: '100%' }}
              slots={mergeDesktopDatePickerSlots({ openPickerIcon: EventIcon })}
              slotProps={mergeDesktopDatePickerSlotProps({
                textField: { sx: outlinedPickerFieldSx, variant: 'outlined' },
              })}
            />
          )}
        />
      </ThemeProvider>
    </LocalizationProvider>
  )
}

const RecurrenceBuilder = ({
  initialRule,
  onGenerate,
  truncated,
  hasGenerated,
}) => {
  const classes = useStyles()
  const { control, register, watch } = useFormContext()
  const endType = watch(`${RULE}.endType`, initialRule.endType)
  const startDate = watch(`${RULE}.startDate`, initialRule.startDate)
  const pickerSlotProps = {
    textField: { sx: outlinedPickerFieldSx, variant: 'outlined' },
  }

  return (
    <Box className={classes.builder} data-testid="recurrence-builder">
      <Grid container spacing={2} className={classes.builderRow}>
        <Grid item xs={12} sm={4}>
          <RuleDatePicker
            name={`${RULE}.startDate`}
            label="Starts"
            defaultValue={initialRule.startDate}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <PGATimePicker
            id="recurrence-start-time"
            name={`${RULE}.startTime`}
            defaultValue={initialRule.startTime}
            date={toDateTime(startDate)}
            onChanged={noop}
            label="Start time"
            minutesStep={5}
            format="h:mm a"
            slotProps={pickerSlotProps}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <PGATimePicker
            id="recurrence-end-time"
            name={`${RULE}.endTime`}
            defaultValue={initialRule.endTime}
            date={toDateTime(startDate)}
            onChanged={noop}
            label="End time"
            horizontalAnchor="right"
            minutesStep={5}
            format="h:mm a"
            slotProps={pickerSlotProps}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} className={classes.builderRow}>
        <Grid item xs={12} sm={4}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel id="recurrence-interval-label">
              Repeats every
            </InputLabel>
            <Controller
              name={`${RULE}.intervalWeeks`}
              control={control}
              defaultValue={initialRule.intervalWeeks}
              as={
                <Select
                  labelId="recurrence-interval-label"
                  label="Repeats every"
                  data-testid="recurrence-interval"
                >
                  {[1, 2, 3, 4].map(weeks => (
                    <MenuItem key={weeks} value={weeks}>
                      {weeks === 1 ? 'Week' : `${weeks} weeks`}
                    </MenuItem>
                  ))}
                </Select>
              }
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={8}>
          <Typography className={classes.label}>On</Typography>
          <Controller
            name={`${RULE}.weekdays`}
            control={control}
            defaultValue={initialRule.weekdays}
            render={({ value, onChange }) => (
              <ToggleButtonGroup
                value={value}
                onChange={(_, next) => {
                  if (next.length) onChange(next)
                }}
                aria-label="Days of the week"
                className={classes.weekdays}
              >
                {WEEKDAYS.map(day => (
                  <ToggleButton
                    key={day.value}
                    value={day.value}
                    aria-label={day.label}
                    className={classes.weekday}
                  >
                    {day.short}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
          />
        </Grid>
      </Grid>

      <Box className={classes.builderRow}>
        <Typography className={classes.label}>Ends</Typography>
        <Controller
          name={`${RULE}.endType`}
          control={control}
          defaultValue={initialRule.endType}
          render={({ value, onChange }) => (
            <RadioGroup
              row
              value={value}
              onChange={e => onChange(e.target.value)}
              className={classes.endsRow}
            >
              <Box className={classes.endsOption}>
                <FormControlLabel
                  value={END_TYPE_DATE}
                  control={<Radio color="primary" />}
                  label="On"
                />
                <Box className={classes.endDatePicker}>
                  <RuleDatePicker
                    name={`${RULE}.endDate`}
                    label="End date"
                    defaultValue={initialRule.endDate}
                  />
                </Box>
              </Box>
              <Box className={classes.endsOption}>
                <FormControlLabel
                  value={END_TYPE_COUNT}
                  control={<Radio color="primary" />}
                  label="After"
                />
                <TextField
                  name={`${RULE}.count`}
                  inputRef={register()}
                  defaultValue={initialRule.count}
                  type="number"
                  variant="outlined"
                  size="small"
                  disabled={endType !== END_TYPE_COUNT}
                  inputProps={{
                    min: MIN_RECURRING_DATES,
                    max: MAX_RECURRING_DATES,
                    'aria-label': 'Number of dates',
                  }}
                  className={classes.countInput}
                />
                <Typography variant="body2">dates</Typography>
              </Box>
            </RadioGroup>
          )}
        />
      </Box>

      <Box className={classes.generateRow}>
        <TonalButton
          type="button"
          onClick={onGenerate}
          startIcon={<RepeatIcon />}
          data-testid="generate-dates"
        >
          {hasGenerated ? 'Regenerate dates' : 'Generate dates'}
        </TonalButton>
        {truncated ? (
          <Typography variant="body2" color="textSecondary">
            Only the first {MAX_RECURRING_DATES} dates were added.
          </Typography>
        ) : null}
      </Box>
    </Box>
  )
}

/**
 * Renders the recurrence builder plus one editable row per date, backed by the
 * form's `sessions` field array (each row becomes its own group event).
 */
const RecurringDatesInput = ({
  name = 'sessions',
  timezone,
  showBuilder = true,
  pastDateCount = 0,
}) => {
  const classes = useStyles()
  const formClasses = useFormStyles()
  const { control, errors, getValues, register, setValue } = useFormContext()
  const { fields, append, remove } = useFieldArray({
    control,
    name,
    keyName: 'fieldKey',
  })
  const [truncated, setTruncated] = useState(false)
  // Replacing rows is two renders: clear the rows, then set the new dates.
  // A setValue over mounted rows lets react-hook-form write the old row values
  // back into the new array, and on first mount (switching from Series) the
  // unmounting Series field array unregisters `sessions`, dropping a setValue.
  const [pendingDates, setPendingDates] = useState(null)
  // In the create flow the builder is shown; keep the date list hidden until
  // the coach clicks Generate so the page isn't overwhelming on first view.
  // If the form already contains enough pre-existing rows (e.g. preloaded
  // recurring data), show the list immediately.
  const [hasGenerated, setHasGenerated] = useState(
    !showBuilder || fields.length >= MIN_RECURRING_DATES,
  )

  const initialRule = useMemo(
    () => ({
      ...defaultRecurrenceRule(getValues(name)?.[0]),
      ...getValues(RULE),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const replaceDates = occurrences => {
    remove()
    setPendingDates(occurrences)
  }

  useEffect(() => {
    if (!pendingDates) return
    setValue(name, pendingDates, { shouldDirty: true })
    setPendingDates(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDates])

  const handleGenerate = () => {
    setHasGenerated(true)
    // getValues(RULE) returns cached state rather than the registered inputs,
    // so read each rule field by its full name.
    const liveRule = getValues(RULE_FIELDS.map(key => `${RULE}.${key}`))[RULE]
    const rule = { ...initialRule, ...omitBy(liveRule, isUndefined) }
    const result = generateOccurrences(rule)
    setTruncated(result.truncated)
    replaceDates(result.occurrences)
  }

  const addDate = () => {
    const rows = getValues(name) || []
    const last = rows[rows.length - 1]
    const lastDate = toDateTime(last?.startDate)
    const lastStart = toDateTime(last?.startTime)
    const lastEnd = toDateTime(last?.endTime)
    if (!lastDate || !lastStart || !lastEnd) return

    const next = lastDate.plus({ weeks: 1 })
    append({
      startDate: next,
      startTime: lastStart.plus({ weeks: 1 }),
      endTime: lastEnd.plus({ weeks: 1 }),
    })
  }

  const fieldError = errors?.[name]
  const listError = fieldError?.message

  return (
    <Box className={formClasses.fieldGroup}>
      <Typography className={formClasses.sectionHeading}>
        Repeat schedule
      </Typography>
      <Typography className={formClasses.fieldHelper}>
        {showBuilder
          ? 'Set how often this event repeats, then fine-tune the dates. '
          : 'Changes apply to every upcoming date. '}
        Each date is a separate event with its own registration.
      </Typography>

      {showBuilder ? (
        <RecurrenceBuilder
          initialRule={initialRule}
          onGenerate={handleGenerate}
          truncated={truncated}
          hasGenerated={hasGenerated}
        />
      ) : null}

      {!hasGenerated ? (
        <Typography variant="body2" className={classes.error}>
          Click Generate dates to create the recurring schedule.
        </Typography>
      ) : null}

      {hasGenerated ? (
        <>
          <Box className={classes.listHeader}>
            <Typography
              className={classes.count}
              data-testid="recurring-date-count"
            >
              {fields.length} {pluralize(fields.length, 'date')}
            </Typography>
            {pastDateCount > 0 ? (
              <Typography variant="body2" color="textSecondary">
                {pastDateCount} past {pluralize(pastDateCount, 'date')} won't
                change
              </Typography>
            ) : null}
          </Box>
          {listError ? (
            <Typography variant="body2" className={classes.error}>
              {listError}
            </Typography>
          ) : null}

          {fields.map((field, index) => (
            <Box key={field.fieldKey}>
              <input
                type="hidden"
                name={`${name}[${index}].groupEventId`}
                ref={register()}
                defaultValue={field.groupEventId || ''}
              />
              <SessionInput
                session={field}
                timezone={timezone}
                handleVerifySessions={noop}
                onDelete={remove}
                index={index}
                name={name}
                fields={fields}
                variant="edit"
                dateFormat={DATE_ROW_FORMAT}
              />
              {field.registrationCount > 0 ? (
                <Typography className={classes.rowMeta}>
                  {field.registrationCount}{' '}
                  {pluralize(field.registrationCount, 'registrant')}. Removing
                  this date cancels it and emails them.
                </Typography>
              ) : null}
            </Box>
          ))}

          {fields.length < MAX_RECURRING_DATES ? (
            <TonalButton
              type="button"
              onClick={addDate}
              startIcon={<AddOutlinedIcon />}
              data-testid="add-recurring-date"
            >
              Add date
            </TonalButton>
          ) : null}
        </>
      ) : null}
    </Box>
  )
}

RecurringDatesInput.propTypes = {
  name: PropTypes.string,
  timezone: PropTypes.string,
  showBuilder: PropTypes.bool,
  pastDateCount: PropTypes.number,
}

export default RecurringDatesInput
